import axios from 'axios';

import * as api from './apiClient';
import { generateInstallationId } from './installationIdentity';
import * as storage from './storage';

/**
 * Durable client event queue. Events get a UUID `event_id`, are persisted before any network
 * call, sent in batches, and removed only after the server answered for them: `accepted`,
 * `duplicate` and `rejected_permanent` leave the queue; `retryable` stays. Bounded in count and
 * bytes, with a backoff after a failed flush. Cleared on user change so nothing of the previous
 * user is attributed to the next one. Never blocks the UI; never holds a target or personal data.
 *
 * The queue lives in `localStorage`, which every tab of the site shares, so it is read back before
 * each change instead of being held in memory: a tab never overwrites what another one queued.
 */
export const STORAGE_KEY = 'EVENT_QUEUE';
const MAX_EVENTS = 500;
const MAX_BYTES = 200 * 1024;
const BATCH = 100;
const BACKOFF_MS = [5000, 15000, 60000, 300000];

const state = {
  flushing: null,
  failures: 0,
  nextFlushAt: 0,
};

const log = (...args) => console.log('[NotificationsV2][events]', ...args);

const load = () => {
  const parsed = storage.readJson(STORAGE_KEY, []);
  return Array.isArray(parsed) ? parsed : [];
};

const persist = items => {
  storage.writeJson(STORAGE_KEY, items);
};

const trim = items => {
  while (items.length > MAX_EVENTS) {
    items.shift();
  }
  while (items.length > 0 && JSON.stringify(items).length > MAX_BYTES) {
    items.shift();
  }
  return items;
};

/**
 * @param {Object} event  { event_type, notification_id?, delivery_id?, session_id?, receipt_token?, metadata?, occurred_at? }
 * @returns {string} the event id
 */
export const enqueue = async event => {
  const item = {
    event_id: generateInstallationId(),
    occurred_at: new Date().toISOString(),
    ...event,
  };
  persist(trim([...load(), item]));
  return item.event_id;
};

const hasSession = () => !!axios.defaults.headers.common.Authorization;

/** Sends what can be sent now: JWT events with a session, receipt-token events always. */
export const flush = async ({ force = false } = {}) => {
  if (state.flushing) {
    return state.flushing;
  }
  if (!force && Date.now() < state.nextFlushAt) {
    return { skipped: 'backoff' };
  }

  state.flushing = (async () => {
    const items = load();
    if (items.length === 0) {
      return { sent: 0 };
    }

    const session = hasSession();
    const viaJwt = session ? items.slice(0, BATCH) : [];
    const viaReceipt = session
      ? []
      : items.filter(e => e.receipt_token).slice(0, BATCH);
    const batch = viaJwt.length ? viaJwt : viaReceipt;

    if (batch.length === 0) {
      return { skipped: 'no_session' };
    }

    try {
      const send = viaJwt.length ? api.postEvents : api.postReceiptEvents;
      const data = await send(
        batch.map(e => ({
          event_id: e.event_id,
          event_type: e.event_type,
          occurred_at: e.occurred_at,
          notification_id: e.notification_id || undefined,
          delivery_id: e.delivery_id || undefined,
          session_id: e.session_id || undefined,
          receipt_token: viaJwt.length ? undefined : e.receipt_token,
          metadata: e.metadata || undefined,
        })),
      );
      const results = new Map(
        (data?.results || []).map(r => [r.event_id, r.result]),
      );
      const done = new Set();

      batch.forEach(e => {
        const result = results.get(e.event_id);
        if (result && result !== 'retryable') {
          done.add(e.event_id);
        }
      });

      const remaining = load().filter(e => !done.has(e.event_id));
      persist(remaining);
      state.failures = 0;
      state.nextFlushAt = 0;
      return { sent: done.size, remaining: remaining.length };
    } catch (err) {
      const refusal = api.refusalOf(err);
      const status = err?.response?.status;

      // A 4xx that is not a rate limit will not get better by retrying the same batch.
      if (status && status >= 400 && status < 500 && status !== 429) {
        const ids = new Set(batch.map(e => e.event_id));
        persist(load().filter(e => !ids.has(e.event_id)));
        log('batch rejected', status, refusal);
        return { dropped: batch.length };
      }

      state.failures += 1;
      state.nextFlushAt =
        Date.now() +
        BACKOFF_MS[Math.min(state.failures - 1, BACKOFF_MS.length - 1)];
      log('flush failed', refusal || err?.message);
      return { error: refusal || err?.message };
    } finally {
      state.flushing = null;
    }
  })();

  return state.flushing;
};

/** On logout or user change: events of the previous user are never attributed to the next. */
export const clear = async () => {
  persist([]);
  state.failures = 0;
  state.nextFlushAt = 0;
};

export const size = async () => load().length;

/** Tests only. */
export const __reset = () => {
  Object.assign(state, {
    flushing: null,
    failures: 0,
    nextFlushAt: 0,
  });
};
